import { useState, useEffect, useRef } from "react";
import "./UpdateTicket.css";
import { DateTime } from "luxon";
import { sanitize } from "../Messages/Messages.js";
import { ButtonsForUpdateTicket, Constants } from "../Utils/Constants.js";
import { AdditionalDetails } from "./AdditionalDetails.js";
import { FollowUpDetails } from "./FollowUpDetails.js";
import {RenderDecisionSection} from "./DecisionTicket.js";
import {TicketList} from "./Ticketlist.js";
import { SingleTicketDetails } from "./SingleTicketDetails.js";

export const UpdateTicket = ({ currentIndex, allMessages, setIsDisabled, socket, messages, onTicketClick, changeTicketClick, askTicketId, setBackTickets, singleTicketDetails, setSingleTicketDetails, uirehydrating, isUiDisabled, onAddDetails, onSubmitDecision, onCloseAllTickets, handleModalResponse, setLoader, ticketAttachment, setIsAttachmentEnabled, setIsPreviewUrlEnable, setAttachmentData, setPreviewUrl, fileInputRef, getFileIcon, setUploadError, changeticket }) => {
  const [tickets, setTickets] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [followUpDetails, setFollowUpDetails] = useState("");
  const [showDetailsInput, setShowDetailsInput] = useState(false);
  const [showFollowUpInput, setShowFollowUpInput] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);
  const [askTicketID, setAskTicketID] = useState(false);
  const [isLoadingTicketActions, setIsLoadingTicketActions] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [lastActionType, setLastActionType] = useState(null);
  const [doesChanged, setDoesChanged] = useState(false);
  const [lastTicketClickTime, setLastTicketClickTime] = useState(null);
  const [fileHoverIndex, setFileHoverIndex] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [description, setDescription] = useState("");
  const [triggerBack, setTriggerBack] = useState(false);
  const [DecisionDetails, setDecisionDetails] = useState(false);

  const bottomRef = useRef(null);
  const ticketsRef = useRef(null);

  const ticketCount = Array.isArray(tickets) ? tickets.length : 0;
  const safeMessages = Array.isArray(allMessages) ? allMessages : [];

  const isDecisionTicket = (singleTicketDetails?.formattedReference?.startsWith("D") && singleTicketDetails?.eventStatusEnum === 'OPEN');

  const isAdditionalSubmitDisabled = (!additionalDetails.trim() && !ticketAttachment?.length) ||loadingDetails || isUiDisabled;
  const isFollowupSubmitDisabled = (!followUpDetails.trim() && !ticketAttachment?.length) || loadingFollowUp || isUiDisabled;
  const isClosedTicket = singleTicketDetails?.eventStatusEnum !== 'OPEN';

  const resetTicketsUI = (ticketsData) => {
      setTickets(ticketsData);
      setVisibleCount(5);
      setShowDetailsInput(false);
      setShowFollowUpInput(false);
  };

  useEffect(() => {
    if (messages?.uiType === "ticket" && Array.isArray(messages?.data)) {
      resetTicketsUI(messages.data);
    }
  }, [messages]);

  useEffect(() => {
    setShowDetailsInput(false);
    setShowFollowUpInput(false);
    setAdditionalDetails("");
    setFollowUpDetails("");
    setLoadingDetails(false);
    setLoadingFollowUp(false);
    setShowContinuePrompt(false);
    setLastActionType(null);
    setIsLoadingTicketActions(false);
    if(!uirehydrating){
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
}}, [singleTicketDetails]);

  useEffect(() => {
    if(!uirehydrating){
    ticketsRef.current?.scrollIntoView({ behavior: "smooth" });
}}, [tickets, visibleCount, doesChanged, showDetailsInput, showFollowUpInput]);

  useEffect(() => {
    if (singleTicketDetails) {
      if (singleTicketDetails.actions) {
        setIsLoadingTicketActions(false);
      } else {
        setIsLoadingTicketActions(true);
      }
    }
  }, [singleTicketDetails]);

  useEffect(() => {
    if (singleTicketDetails && singleTicketDetails.actions) {
      setIsLoadingTicketActions(false);
    }
  }, [singleTicketDetails?.actions]);

  useEffect(() => {
    let timeoutId;
    if (isLoadingTicketActions) {
      timeoutId = setTimeout(() => {
        setIsLoadingTicketActions(false);
        console.log("Auto-disabled loading state after timeout");
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoadingTicketActions]);

  useEffect(() => {    
    if ((loadingDetails || loadingFollowUp) && Array.isArray(safeMessages) && safeMessages.length > 0) {
      const lastMessage = safeMessages[safeMessages.length - 1];

      if (
        lastMessage &&
        lastMessage.from === "bot" &&
        lastMessage.text &&
        lastMessage.text.includes("successfully added")
      ) {
        console.log("Detected success message, showing continue prompt");

        setShowDetailsInput(false);
        setShowFollowUpInput(false);
        setAdditionalDetails("");
        setFollowUpDetails("");
        setLoadingDetails(false);
        setLoadingFollowUp(false);
        setShowContinuePrompt(true);
        
        if (loadingDetails) setLastActionType("details");
        if (loadingFollowUp) setLastActionType("followup");
      }
    }
    if(triggerBack){
    setIsDisabled(false)
    setTriggerBack(false);
    }
  }, [safeMessages, loadingDetails, loadingFollowUp, setIsDisabled, triggerBack]);

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 5, ticketCount));
    setDoesChanged(true);
  };

  const handleDecisionCancel = () => {
    if (isUiDisabled) return;
    setSelectedAnswer("");
    setDescription("");
  };

  const handleAddDetailsClick = () => {
    if (isUiDisabled) return;
    setUploadError("");
    setIsPreviewUrlEnable(true)
    setIsAttachmentEnabled(true)
    setShowDetailsInput(true);
    setShowFollowUpInput(false);
    setShowContinuePrompt(false);
  };

  const handleFollowUpClick = () => {
    if (isUiDisabled) return;
    setUploadError("");
    setIsPreviewUrlEnable(true)
    setIsAttachmentEnabled(true)
    setShowFollowUpInput(true);
    setShowDetailsInput(false);
    setShowContinuePrompt(false);
  };

  const handleDecisionSubmit = () => {
    if (isUiDisabled) return;
    setIsDisabled(true)
    setDecisionDetails(true)
    onSubmitDecision(
      singleTicketDetails.id,
      selectedAnswer,
      description,
    )
    setLoader(true);
  };

  const handleSubmitDetails = (type) => {
    if (isUiDisabled) return;
    if (type === "details" && (additionalDetails.trim() || ticketAttachment) && singleTicketDetails) {
      setIsDisabled(true);
      setLoadingDetails(true);
      onAddDetails(
        singleTicketDetails.id,
        additionalDetails.trim() || "attachment uploaded",
        42,
        ticketAttachment
      );
    } else if (type === "followup" && (followUpDetails.trim() || ticketAttachment) && singleTicketDetails) {
      setLoadingFollowUp(true);
      onAddDetails(
        singleTicketDetails.id,
        followUpDetails.trim() || "attachment uploaded",
        40,
        ticketAttachment
      );
    }
  };

  const handleCancelDetails = (type) => {
    if (type === "details") {
      setUploadError("");
      setAttachmentData([])
      setPreviewUrl([])
      setIsAttachmentEnabled(false)
      setShowDetailsInput(false);
      setAdditionalDetails("");
      setLoadingDetails(false);
    } else if (type === "followup") {
      setUploadError("");
      setAttachmentData([])
      setPreviewUrl([])
      setIsAttachmentEnabled(false)
      setShowFollowUpInput(false);
      setFollowUpDetails("");
      setLoadingFollowUp(false);
    }
    setShowContinuePrompt(false);
  };

  const handleBack = () => {
    if (setBackTickets?.length > 0) {
      messages.data = null;
      singleTicketDetails=null
      setTickets(setBackTickets)
      setVisibleCount(5);
    }
    else {
      socket.current.emit("getTickets")
      setLoader(true);
    }
    setShowDetailsInput(false);
    setShowFollowUpInput(false);
    setAdditionalDetails("");
    setFollowUpDetails("");
    setLoadingDetails(false);
    setLoadingFollowUp(false);
    setShowContinuePrompt(false);
    setIsDisabled(true);
    setSingleTicketDetails(null);
    setIsLoadingTicketActions(false);
    setTriggerBack(true);
  };

  const handleContinueYes = () => {
    if (isUiDisabled) return;

    setShowContinuePrompt(false);
    if (lastActionType === "details") {
      setShowDetailsInput(true);
    } else if (lastActionType === "followup") {
      setShowFollowUpInput(true);
    }
    setLastActionType(null);
  };

  const handleContinueNo = () => {
    setShowContinuePrompt(false);
    setLastActionType(null);
    if (onCloseAllTickets) {
      onCloseAllTickets();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const userZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = DateTime.fromISO(dateString).setZone(userZone);
    return date.toFormat("MM-dd-yyyy HH:mm");
  };

  const handleTicketCardClick = (ticketId) => {
    if (isUiDisabled) return;
    setIsDisabled(true)
    setIsLoadingTicketActions(true);
    setLastTicketClickTime(Date.now());
    if (changeticket) {
      changeTicketClick(ticketId);
    } else {
      onTicketClick(ticketId);
    }
  };
  const removeFile = (index) => {
    setPreviewUrl((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      if (updated?.length === 0 && fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return updated;
    });

    setAttachmentData((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };


  return (
    <div className="update-ticket-container">
      {/* Ticket list rendering. */} 
      {!singleTicketDetails && ticketCount > 0 && (
        <>
          <TicketList tickets={tickets} visibleCount={visibleCount} ticketsRef={ticketsRef} isUiDisabled={isUiDisabled} handleTicketCardClick={handleTicketCardClick} formatDate={formatDate}/>
          {/* Ticket list rendering more than 5 tickets. */} 
          {ticketCount > 5 && visibleCount < ticketCount && (
            <div className={`d-flex col-md-6 col-12 gap-2 justify-content-end mb-2 mt-2 ${isUiDisabled ? "disabled-ticket" : ""}`}>
              <button
                onClick={loadMore}
                className="btn btn-outline-secondary button-hover"
                style={{ color: "#4472c4", borderColor: "#4472c4", minWidth: "120px", fontSize: "14px" }}
                disabled={isUiDisabled}
              >
                {ticketCount >= 10 ? "Show Next 5" : "Show remaining"}
              </button>
            </div>
          )}
        </>
      )}
       {/* Ticket Details rendering. */} 
      {singleTicketDetails && (
        <div className="ticket-details-container">
        < SingleTicketDetails singleTicketDetails={singleTicketDetails} formatDate={formatDate} isDecisionTicket={isDecisionTicket}/>
          {/* Ticket Updates. */} 
          <div className="summary-section">
            <span className="summary-label">Ticket Updates: Here are the top recent updates. Click on above ticket link to view additional updates and details</span>
            {isLoadingTicketActions ? (
              <div className="loading-actions text-center p-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span>Loading ticket updates...</span>
              </div>
            ) : singleTicketDetails.actions && singleTicketDetails.actions.length > 0 ? (
              singleTicketDetails.actions.map((action, index) => (
                <div ref={bottomRef} className="event-row" key={index}>
                  <div className="event-icon"></div>
                  <div className="event-time">{formatDate(action.date)}</div>
                  <div className="event-title">{action.actionType}</div>
                  <div className="event-desc">{action.remarks}</div>
                </div>
              ))
            ) : (
              <div className="no-actions text-center p-3">
                <span>No updates available for this ticket.</span>
              </div>
            )}
          </div>
                            
          {/* Decision ticket buttons. */} 
          {isDecisionTicket && !DecisionDetails && (  
              <RenderDecisionSection singleTicketDetails={singleTicketDetails} selectedAnswer={selectedAnswer} setSelectedAnswer={setSelectedAnswer} isUiDisabled={isUiDisabled} description={description} setDescription={setDescription} loadingDetails={loadingDetails} ButtonsForUpdateTicket={ButtonsForUpdateTicket} handleDecisionSubmit={handleDecisionSubmit} handleBack={handleBack} handleDecisionCancel={handleDecisionCancel} />
          )}
              {/* Normal ticket Buttons. */} 
            {!showContinuePrompt && !showDetailsInput && !showFollowUpInput && !isDecisionTicket && (
              <div
                ref={ticketsRef}
                className={`d-flex col-md-6 col-12 gap-2 justify-content-center mb-2 mt-2 mx-auto ${isUiDisabled ? "disabled-ticket" : ""}`}
              >
              
              {/* Added third argument (Integer) to slice the text in the buttons (Some text disrupts the button text). */}              
              {!isClosedTicket && ButtonsForUpdateTicket(handleAddDetailsClick, (loadingDetails ? "Adding Details..." : "Provide additional information for this ticket"), 3)}
              {!isClosedTicket && ButtonsForUpdateTicket(handleFollowUpClick, (loadingFollowUp ? "Adding Follow-up..." : "Request a follow-up on this ticket"), 6)}              
              {ButtonsForUpdateTicket(handleBack, "Go back to my tickets", 4)}
              {ButtonsForUpdateTicket(handleModalResponse, "Go to main menu", 4)}             
              </div>
            )}
            {/* Additional details. */} 
            {showDetailsInput && (
              <AdditionalDetails ticketsRef={ticketsRef} ticketAttachment={ticketAttachment} fileHoverIndex={fileHoverIndex} setFileHoverIndex={setFileHoverIndex} additionalDetails={additionalDetails} getFileIcon={getFileIcon} removeFile={removeFile} setAdditionalDetails={setAdditionalDetails} loadingDetails={loadingDetails} isUiDisabled={isUiDisabled} handleSubmitDetails={handleSubmitDetails} handleCancelDetails={handleCancelDetails} isAdditionalSubmitDisabled={isAdditionalSubmitDisabled} />
            )}
            {/* Follow-up details. */}
            {showFollowUpInput && (
              <FollowUpDetails ticketsRef={ticketsRef} ticketAttachment={ticketAttachment} fileHoverIndex={fileHoverIndex} setFileHoverIndex={setFileHoverIndex} removeFile={removeFile} setFollowUpDetails={setFollowUpDetails} loadingFollowUp={loadingFollowUp} isUiDisabled={isUiDisabled} handleSubmitDetails={handleSubmitDetails} handleCancelDetails={handleCancelDetails} followUpDetails={followUpDetails} isFollowupSubmitDisabled={isFollowupSubmitDisabled} />
            )}
          </div>
      )}
    </div>
  );
};